import { useMemo, useRef } from 'react';
import { BookOffer } from 'xrpl';
import { Offer } from './offer';
import { SpreadSeparator } from './spread-separator';

type OffersProps = {
  sellOffers: BookOffer[];
  buyOffers: BookOffer[];
};

/**
 * Component to display the buy and sell offers
 * @param sellOffers Array of BookOffer objects
 * @param buyOffers Array of BookOffer objects
 * @returns React.ReactNode
 */
export const Offers = ({ sellOffers, buyOffers }: OffersProps) => {
  const prevSellSequencesRef = useRef<Set<number>>(new Set());
  const prevBuySequencesRef = useRef<Set<number>>(new Set());
  const isInitialMountRef = useRef(true);

  const [sortedSellOffers, sortedBuyOffers] = useMemo(() => {
    const DISPLAY_LIMIT = 20; // Show top 20 best offers on each side
    return [
      sellOffers.sort((a, b) => Number(b?.quality ?? 0) - Number(a?.quality ?? 0)).slice(0, DISPLAY_LIMIT),
      buyOffers.sort((a, b) => Number(a?.quality ?? 0) - Number(b?.quality ?? 0)).slice(0, DISPLAY_LIMIT),
    ];
  }, [sellOffers, buyOffers]);

  // Detect new sell offers (skip on initial mount)
  const currentSellSequences = new Set(sortedSellOffers.map((o) => o.Sequence));
  const newSellSequences = new Set(
    isInitialMountRef.current
      ? []
      : [...currentSellSequences].filter((seq) => !prevSellSequencesRef.current.has(seq))
  );
  prevSellSequencesRef.current = currentSellSequences;

  // Detect new buy offers (skip on initial mount)
  const currentBuySequences = new Set(sortedBuyOffers.map((o) => o.Sequence));
  const newBuySequences = new Set(
    isInitialMountRef.current
      ? []
      : [...currentBuySequences].filter((seq) => !prevBuySequencesRef.current.has(seq))
  );
  prevBuySequencesRef.current = currentBuySequences;

  // Mark that we've passed initial mount
  if (isInitialMountRef.current) {
    isInitialMountRef.current = false;
  }

  // Get top offers for spread calculation
  const topSellOffer = sortedSellOffers[sortedSellOffers.length - 1]; // Lowest price sell (best ask)
  const topBuyOffer = sortedBuyOffers[0]; // Highest price buy (best bid)

  // Calculate max totals for depth visualization
  const maxSellTotal = useMemo(() => {
    if (sortedSellOffers.length === 0) return 0;
    const totals = sortedSellOffers.map((offer) => {
      const { TakerGets } = offer;
      const getValue = (val: string | { value: string }) =>
        typeof val === 'string' ? Number(val) / 1_000_000 : Number(val.value);
      const total = getValue(TakerGets);
      return total;
    });
    return Math.max(...totals);
  }, [sortedSellOffers]);

  const maxBuyTotal = useMemo(() => {
    if (sortedBuyOffers.length === 0) return 0;
    const totals = sortedBuyOffers.map((offer) => {
      const { TakerPays } = offer;
      const getValue = (val: string | { value: string }) =>
        typeof val === 'string' ? Number(val) / 1_000_000 : Number(val.value);
      const total = getValue(TakerPays);
      return total;
    });
    return Math.max(...totals);
  }, [sortedBuyOffers]);

  return (
    <>
      {sortedSellOffers.map((offer, index) => (
        <Offer
          key={`sell-${offer.Account}-${offer.Sequence}-${index}`}
          offer={offer}
          offerType="sell"
          isNew={newSellSequences.has(offer.Sequence)}
          maxTotal={maxSellTotal}
        />
      ))}
      <SpreadSeparator topSellOffer={topSellOffer} topBuyOffer={topBuyOffer} />
      {sortedBuyOffers.map((offer, index) => (
        <Offer
          key={`buy-${offer.Account}-${offer.Sequence}-${index}`}
          offer={offer}
          offerType="buy"
          isNew={newBuySequences.has(offer.Sequence)}
          maxTotal={maxBuyTotal}
        />
      ))}
    </>
  );
};
